import { useTranslation } from 'module/hooks/use-translation';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { NodeViewContent } from '@tiptap/react';
import {
  CustomSectionState,
  MultiQuestionCustomSectionAttributes,
} from 'api/custom-section/types';
import { answerFrom } from 'api/custom-section/types/multi-question';
import { useCallback, useState } from 'react';
import { MultiQuestionPromptType } from 'components/organisms/document-editor/extensions/custom-section/types';
import {
  SectionPreview,
  SectionTitle,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { PromptInput } from './prompt-input';
import { MultiQuestionSectionComponentProps } from '../types';

export const MultiQuestionPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  allowResolve,
  contentEditable,
  onOpen,
  updateContent,
  pageType,
  pageTriggerListFilter,
}: MultiQuestionSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;

  const constructPrompt = useCallback(
    (attributes: MultiQuestionCustomSectionAttributes) => {
      const payload = {
        ...attributes.config,
      };

      return {
        title: attributes.title,
        questions: payload.questions.map((question, index) => {
          const optionType = question.dataType ?? TableDataType.TEXT;

          return {
            label: question.question,
            value: index,
            position: index + 1,
            fieldOptionMeta: {
              choices:
                question.choices?.map((choice, choiceIndex) => ({
                  label: choice,
                  value: choiceIndex,
                })) ?? [],
              id: question.id,
              optionType,
              aiContext: question.aiContext,
              answerFrom: question.answerFrom ?? answerFrom.Customer,
              answerLength:
                optionType === TableDataType.TEXT
                  ? (question.answerLength ?? null)
                  : null,
              generateNewChoices: question.generateNewChoices ?? false,
            },
          };
        }),
      };
    },
    [],
  );
  const [prompt, setPrompt] = useState<MultiQuestionPromptType>(
    constructPrompt(node.attrs),
  );

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );

  const onGenerate = async (userPrompt: MultiQuestionPromptType) => {
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      config: {
        questions: userPrompt.questions.map((question) => {
          const dataType =
            question.fieldOptionMeta?.optionType ?? TableDataType.TEXT;

          return {
            id: question.fieldOptionMeta.id,
            question: question.label,
            generateNewChoices:
              question.fieldOptionMeta?.generateNewChoices ?? false,
            dataType,
            choices:
              question.fieldOptionMeta?.choices?.map(
                (choice) => choice.label,
              ) ?? [],
            aiContext: question.fieldOptionMeta.aiContext,
            answerFrom:
              question.fieldOptionMeta.answerFrom ?? answerFrom.Customer,
            answerLength:
              dataType === TableDataType.TEXT
                ? (question.fieldOptionMeta.answerLength ?? null)
                : null,
          };
        }),
      },
      isTableStatic: allowResolve ? true : false,
      state: CustomSectionState.Closed,
    };

    onDone(attributes).then(() => {
      // Create table content with questions and placeholder configs
      const tableContent =
        userPrompt.questions
          ?.map((question, index) => [
            // Row 1: Index and Question
            {
              type: 'tableRow',
              content: [
                {
                  type: 'tableCell',
                  attrs: {
                    width: '10%',
                  },
                  content: [{ type: 'text', text: `${index + 1}.` }],
                },
                {
                  type: 'tableCell',
                  content: [{ type: 'text', text: question.label }],
                },
              ],
            },
            // Row 2: Empty first cell, placeholder config in second cell
            {
              type: 'tableRow',
              content: [
                {
                  type: 'tableCell',
                  content: [],
                },
                {
                  type: 'tableCell',
                  content: [
                    {
                      type: 'RL_PLACEHOLDER_CONFIG',
                      attrs: {
                        icon: '',
                        iconPosition: 'left',
                      },
                      content: [{ type: 'text', text: 'Answer' }],
                    },
                  ],
                },
              ],
            },
          ])
          .flat() || [];

      updateContent(
        [
          {
            type: 'table',
            content: tableContent,
          },
        ],
        attributes,
      );
    });
  };

  const getNodeAsPayload = useCallback(() => {
    const payload = node.toJSON();

    if (!('content' in payload)) {
      payload.content = [];
    }

    return payload;
  }, [node]);

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, constructPrompt, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          onCancel={() => {
            onRemove();
          }}
          onUndo={onClose}
          getNodeAsPayload={getNodeAsPayload}
          allowResolve={allowResolve}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <SectionPreview
          onClick={() => {
            onOpen();
          }}
        >
          <div>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>
          </div>
          <NodeViewContent />
        </SectionPreview>
      )}
    </div>
  );
};
